import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, map } from 'rxjs';

import { ApiCommonService } from '@api-services/api-common.service';
import { API_ROUTE_BASE } from '@constants/api-routes.constant';
import { ApiAlertError } from '@decorators/api-alert-error';
import { HttpHelper } from '@helpers/http.helper';
import { Signatory, SignatoryJson, SignatorySerialized } from '@models/signatory';

@Injectable({
  providedIn: 'root'
})
export class SignatoryApiService extends ApiCommonService<Signatory> {
  resourceUrl: string = `${API_ROUTE_BASE}/partners/{partnerId}/signatories`;
  buResourceUrl: string = `${API_ROUTE_BASE}/business_units/{businessUnitId}/signatories`;

  fromJson(json: SignatoryJson): Signatory {
    return Signatory.fromJson(json);
  }

  toJson(model: Signatory): SignatorySerialized {
    return Signatory.toJson(model);
  }

  @ApiAlertError()
  getPartnerSignatories(partnerId: number, searchTerms?: string): Observable<Signatory[]> {
    const url: string = HttpHelper.replacePartnerUrl(this.resourceUrl, partnerId);
    const params: HttpParams = HttpHelper.setQParam(searchTerms);
    return super.getAll({ params }, url);
  }

  @ApiAlertError()
  getBusinessUnitSignatories(businessUnitId: number, searchTerms: string): Observable<Signatory[]> {
    const url: string = HttpHelper.replaceBusinessUnitUrl(this.buResourceUrl, businessUnitId);
    const params: HttpParams = HttpHelper.setQParam(searchTerms);
    return super.getAll({ params }, url);
  }

  @ApiAlertError()
  getLegalStructureSignatories(legalStructureId: number): Observable<Signatory[]> {
    const url: string = `${API_ROUTE_BASE}/legal_structures/${legalStructureId}/signatories`;

    return super.getAll(null, url);
  }

  @ApiAlertError()
  createSignatory(partnerId: number, signatory: Signatory): Observable<Signatory> {
    const url: string = HttpHelper.replacePartnerUrl(this.resourceUrl, partnerId);
    const body: SignatorySerialized = this.toJson(signatory);
    return this.http.post(url, body).pipe(map((signatoryJson: SignatoryJson) => this.fromJson(signatoryJson)));
  }

  @ApiAlertError()
  updateSignatory(partnerId: number, signatory: Signatory): Observable<Signatory> {
    const url = `${HttpHelper.replacePartnerUrl(this.resourceUrl, partnerId)}/${signatory.id}`;
    const body: SignatorySerialized = this.toJson(signatory);
    return this.http.patch(url, body).pipe(map((signatoryJson: SignatoryJson) => this.fromJson(signatoryJson)));
  }

  @ApiAlertError()
  createSignatoryScannedSignature(partnerId: number, signatoryId: number, file: File): Observable<void> {
    const url = `${HttpHelper.replacePartnerUrl(
      this.resourceUrl,
      partnerId
    )}/${signatoryId}/signatory_scanned_signatures`;

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<void>(url, formData);
  }

  @ApiAlertError()
  getSignatoryScannedSignaturePreview(partnerId: number, signatoryId: number): Observable<Blob> {
    const url = `${HttpHelper.replacePartnerUrl(
      this.resourceUrl,
      partnerId
    )}/${signatoryId}/signatory_scanned_signatures`;

    return this.http.get(url, { responseType: 'blob' });
  }
}
